import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  Save,
  Settings,
  Event,
  Add,
  Lock,
  Replay,
  AccessTime,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import type { Holiday } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { getCallStatusLabel } from '../utils/callStatus';
import {
  buildSystemSettingsUpdatePayload,
  DEFAULT_PSTN_OUTBOUND_DIAL_TIMEOUT_SECONDS,
  normalizePstnOutboundDialTimeoutSeconds,
} from '../utils/systemSettings';

export default function SettingsPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:settings.title'));
  const { isSuperuser } = useAuth();
  const { language } = useLanguage();

  // Map language code to dayjs locale
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);

  // Check if user can edit system settings (superusers and system admins)
  const canEditSystemSettings = isSuperuser;

  // System Settings state
  const [pstnOutboundDialTimeoutSeconds, setPstnOutboundDialTimeoutSeconds] = useState<number>(
    DEFAULT_PSTN_OUTBOUND_DIAL_TIMEOUT_SECONDS
  );
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [retryStatuses, setRetryStatuses] = useState<string[]>([]);
  const [retryStatusOptions, setRetryStatusOptions] = useState<string[]>([]);
  const [newHolidayDate, setNewHolidayDate] = useState<Dayjs | null>(null);
  const [newHolidayName, setNewHolidayName] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/system-settings/');

      setPstnOutboundDialTimeoutSeconds(
        normalizePstnOutboundDialTimeoutSeconds(
          response.data?.pstnOutboundDialTimeoutSeconds
            ?? response.data?.pstn_outbound_dial_timeout_seconds
        )
      );

      if (response.data.holidays && response.data.holidays.length > 0) {
        setHolidays(response.data.holidays);
      }

      const fetchedRetryStatuses = response.data?.retryStatuses ?? response.data?.retry_statuses;
      if (Array.isArray(fetchedRetryStatuses)) {
        setRetryStatuses(fetchedRetryStatuses);
      }

      const fetchedRetryStatusOptions =
        response.data?.retryStatusOptions ?? response.data?.retry_status_options;
      if (Array.isArray(fetchedRetryStatusOptions)) {
        setRetryStatusOptions(fetchedRetryStatusOptions);
      }
    } catch (err) {
      console.error('Error loading system settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      await api.put(
        '/api/system-settings/',
        buildSystemSettingsUpdatePayload({
          pstnOutboundDialTimeoutSeconds,
          holidays,
          retryStatuses,
        })
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string; detail?: string } } };
      setError(apiError.response?.data?.error || apiError.response?.data?.detail || t('pages:settings.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = () => {
    if (newHolidayDate && newHolidayName) {
      const dateStr = newHolidayDate.format('YYYY-MM-DD');
      setHolidays((prev) =>
        [...prev, { date: dateStr, name: newHolidayName }].sort((a, b) => a.date.localeCompare(b.date))
      );
      setNewHolidayDate(null);
      setNewHolidayName('');
    }
  };

  const handleRemoveHoliday = (date: string) => {
    setHolidays((prev) => prev.filter((h) => h.date !== date));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If user is not a superuser/system admin, show access denied
  if (!canEditSystemSettings) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Settings sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>
              {t('pages:settings.title')}
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ ml: 6 }}>
            {t('pages:settings.subtitle')}
          </Typography>
        </Box>

        <Alert severity="warning" icon={<Lock />} sx={{ maxWidth: 600 }}>
          <Typography variant="body1" fontWeight={600}>
            {t('pages:settings.accessDenied')}
          </Typography>
          <Typography variant="body2">{t('pages:settings.accessDeniedDesc')}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Settings sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>
              {t('pages:settings.title')}
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ ml: 6 }}>
            {t('pages:settings.subtitle')}
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('pages:settings.savedSuccessfully')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Outbound PSTN dial timeout */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTime color="primary" />
              <Typography variant="h6">{t('pages:settings.pstnOutboundDialTimeout')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:settings.pstnOutboundDialTimeoutDesc')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('pages:settings.pstnOutboundDialTimeout')}
              value={pstnOutboundDialTimeoutSeconds}
              onChange={(event) =>
                setPstnOutboundDialTimeoutSeconds(
                  normalizePstnOutboundDialTimeoutSeconds(event.target.value)
                )
              }
              inputProps={{ min: 1, step: 1 }}
              helperText={t('pages:settings.pstnOutboundDialTimeoutHelper')}
            />
          </CardContent>
        </Card>

        {/* Campaign Retry Statuses */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Replay color="primary" />
              <Typography variant="h6">{t('pages:settings.retryStatuses')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:settings.retryStatusesDesc')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('pages:settings.retryStatusesLabel')}</InputLabel>
              <Select
                multiple
                value={retryStatuses}
                onChange={(event) => setRetryStatuses(event.target.value as string[])}
                input={<OutlinedInput label={t('pages:settings.retryStatusesLabel')} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={getCallStatusLabel(value, t)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {retryStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={retryStatuses.includes(status)} />
                    <ListItemText primary={getCallStatusLabel(status, t)} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('pages:settings.retryStatusesHelper')}
              </Typography>
            </FormControl>
          </CardContent>
        </Card>

        {/* Holidays */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Event color="primary" />
              <Typography variant="h6">{t('pages:settings.holidays')}</Typography>
              <Chip size="small" label={holidays.length} color="default" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:settings.holidaysDesc')}
            </Typography>

            {/* Add new holiday */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <DatePicker
                label={t('pages:settings.holidayDate')}
                value={newHolidayDate}
                onChange={(value) => setNewHolidayDate(value)}
                slotProps={{
                  textField: { size: 'small', sx: { width: 180 } },
                }}
              />
              <Box
                component="input"
                type="text"
                placeholder={t('pages:settings.holidayName')}
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddHoliday()}
                sx={{
                  flex: 1,
                  height: 40,
                  px: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  outline: 'none',
                  '&:focus': {
                    borderColor: 'primary.main',
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddHoliday}
                disabled={!newHolidayDate || !newHolidayName}
              >
                {t('common:add')}
              </Button>
            </Paper>

            {/* Holidays list */}
            {holidays.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {holidays.map((holiday) => (
                  <Chip
                    key={holiday.date}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" fontWeight={600}>
                          {dayjs(holiday.date).locale(dayjsLocale).format('DD MMM YYYY')}
                        </Typography>
                        <Typography variant="caption">{holiday.name}</Typography>
                      </Box>
                    }
                    onDelete={() => handleRemoveHoliday(holiday.date)}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('pages:settings.noHolidays')}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSaveSystemSettings}
            disabled={saving}
            size="large"
          >
            {saving ? t('common:saving') : t('pages:settings.saveSystemSettings')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
